import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaCrown, FaArrowRight, FaDownload } from 'react-icons/fa';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setError('No payment session found');
      setVerifying(false);
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      const response = await api.get(`/payment/verify/${sessionId}`);
      
      if (response.data.success) {
        setPaymentData(response.data);
        // Update user context with premium status
        updateUser(response.data.user);
      } else {
        setError(response.data.message || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setError(error.response?.data?.message || 'Failed to verify payment');
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-green-700 text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/lessons')}
            className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto text-center"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="mb-6"
        >
          <div className="relative">
            <FaCheckCircle className="text-8xl text-green-500 mx-auto" />
            <FaCrown className="text-3xl text-yellow-500 absolute -top-2 -right-2" />
          </div>
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            🎉 Payment Successful!
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Welcome to Premium! You now have unlimited access to all our content.
          </p>
        </motion.div>

        {/* Payment Details */}
        {paymentData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6"
          >
            <h3 className="text-lg font-semibold text-green-800 mb-4">Payment Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-600 font-medium">Amount Paid:</span>
                <p className="text-green-800 font-bold">
                  ${(paymentData.payment.amount / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <span className="text-green-600 font-medium">Payment ID:</span>
                <p className="text-green-800 font-mono text-xs">
                  {paymentData.payment.sessionId.substring(0, 20)}...
                </p>
              </div>
              <div>
                <span className="text-green-600 font-medium">Status:</span>
                <p className="text-green-800 font-bold capitalize">
                  {paymentData.payment.status}
                </p>
              </div>
              <div>
                <span className="text-green-600 font-medium">Activated:</span>
                <p className="text-green-800">
                  {new Date(paymentData.user.premiumActivatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Premium Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-8"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            🚀 Your Premium Benefits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            {[
              '✅ Access to all premium videos',
              '🎯 Unlimited video requests',
              '⚡ Priority support',
              '📚 Downloadable resources',
              '🏆 Certificate of completion',
              '♾️ Lifetime access'
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + index * 0.1 }}
                className="flex items-center bg-gray-50 p-3 rounded-lg"
              >
                <span className="text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="space-y-4"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-6 rounded-lg transition-all transform hover:scale-105 flex items-center justify-center"
          >
            Start Learning Now
            <FaArrowRight className="ml-2" />
          </button>
          
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/payment/history')}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
            >
              View Payment History
            </button>
            <button
              onClick={() => window.print()}
              className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
            >
              <FaDownload className="mr-2" />
              Receipt
            </button>
          </div>
        </motion.div>

        {/* Email Confirmation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-6 text-sm text-gray-600"
        >
          📧 A confirmation email has been sent to your registered email address.
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;